import { MdPerson } from 'react-icons/md';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import { isDevEnvironment } from '/lib/helpers';
import authApi from '/api/auth';
import userApi from '/api/user';
import { cookie, goTo, userIs, capitalize, isSupported } from '/lib/helpers';
import Bars from '/components/Bars';
import { redirectToUserLandingPage } from '/pages/auth';
import usePrevious from '/hooks/usePrevious';
import { useUserState } from '/store/userStore';

export default function DevSignInOptions() {
    const user = useUserState((state) => state.user);
    const setUser = useUserState((state) => state.setUser);
    const clearUser = useUserState((state) => state.clearUser);

    const router = useRouter();

    const isAdmin = (user) => userIs(user, 'GLOBAL_ADMIN');

    const [busy, setBusy] = useState(false);
    const [roles, setRoles] = useState([
        { phone: '+4790909000', role: 'Admin - DB seed' },
        { phone: '+48665001225', role: 'Admin - Konrad' },
        { phone: '+4790080514', role: 'Admin - Fredrik ' },
    ]);

    const prevUser = usePrevious(user);

    const enabled =
        isDevEnvironment || process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging';

    useEffect(() => {
        if (!enabled) return;

        document.addEventListener('click', async (e) => {
            const tel = (e.target.getAttribute('href') || '').replace(
                'tel:',
                ''
            );

            if (
                !tel ||
                !~tel.indexOf('+47') ||
                e.target.classList.contains('impersonation-link')
            )
                return;

            e.target.classList.add('impersonation-link');

            e.preventDefault();

            await logOut();
            await logIn(tel);
        });
    }, []);

    useEffect(() => {
        if (roles.length > 5) return;
        const savedRoleOptions = window.localStorage.getItem('role-options');
        if (savedRoleOptions) {
            setRoles([
                ...roles,
                ...Object.values(JSON.parse(savedRoleOptions)),
            ]);
            return;
        }
        if (!isAdmin(prevUser) && isAdmin(user)) {
            fetchRoles();
        }
    }, [user]);

    const logOut = async (redirect) => {
        console.log('Sign out called from  DevSignInOptions');
        setBusy(true);
        await authApi.logOut();
        cookie.set('AuthToken', '', 0);
        cookie.set('ContractorUuid', '', 0);
        clearUser();
        setBusy(false);
        if (redirect) goTo(redirect);
    };

    const logIn = async (tel) => {
        if (!tel) return;
        setBusy(true);
        await authApi.logIn({ phone: tel });
        const resp = await authApi.confirmCode({
            phone_number: tel,
            code: '0000',
        });
        setBusy(false);
        if (resp.error) return alert(resp.error);
        if (resp.token) cookie.set('AuthToken', resp.token, 30);
        if (resp.user.contractor)
            cookie.set('ContractorUuid', resp.user.contractor.uuid, 30);
        setUser(resp.user);
        if (router.query?.redirect) {
            router.replace(router.query?.redirect);
        } else {
            redirectToUserLandingPage(resp.user, goTo);
        }
    };

    const fetchRoles = async () => {
        const userRoles = [
            'CASEWORKER',
            'STORE_EMPLOYEE',
            'CUSTOMER_SERVICE',
            'ELECTRICIAN',
            'CONTRACTOR_ADMIN',
            'CUSTOMER',
            'SALES_PARTNER_SELLER',
        ];
        const resp = await userApi.index(userRoles);
        if (resp.data) {
            const roleOptions = userRoles.reduce(
                (acc, r) => ({
                    ...acc,
                    [r]: resp.data
                        .filter((u) => (u.availableRoles || []).includes(r))
                        .map((u) => ({
                            phone: u.phoneNumber,
                            role: capitalize(
                                r.replace(/_/g, ' ').toLowerCase()
                            ),
                        }))[0],
                }),
                {}
            );

            setRoles([...roles, ...Object.values(roleOptions)]);
            if (isSupported(() => window.localStorage)) {
                window.localStorage.setItem(
                    'role-options',
                    JSON.stringify(roleOptions)
                );
            }
        }
    };

    return (
        <>
            <li>
                <MdPerson />
                {!user ? 'Sign in as...' : user.name}
                {busy && (
                    <Bars bars={20} width={30} opacity={0.1} speed={0.35} />
                )}
                <ul>
                    {roles.map(({ phone, role }, i) => (
                        <li key={i} onClick={() => logIn(phone)}>
                            {role}
                        </li>
                    ))}
                    {user && <li onClick={() => logOut('/')}>Sign out</li>}
                </ul>
            </li>
        </>
    );
}
